<template>
  <div>
    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-1">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="2"
            class="d-flex align-items-end"
          >
            <b-form-group
              :label="$t('partner-admin.filters.limit')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.limit"
                :options="limitOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="3"
            class="ml-auto"
          >
            <b-form-group
              :label="$t('partner-admin.fields.status')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-select
                v-model="query.status"
                :options="statusOptions"
              />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="5"
          >
            <b-form-group
              :label="$t('partner-admin.filters.search')"
              class="mb-1 mb-md-0 w-100"
            >
              <b-form-input v-model="query.q" />
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="2"
            class="ml-auto"
          >
            <b-button
              variant="primary"
              block
              :to="{ name: 'external-partner-create' }"
            >
              {{ $t('partner-admin.actions.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <rc-overlay :show="loading">
        <b-table
          :items="partners"
          :fields="fields"
          responsive
          show-empty
          :empty-text="loading ? $t('shared.loading') : $t('shared.no-data')"
        >
          <template #cell(status)="data">
            <b-badge :variant="data.item.status === 'active' ? 'success' : 'secondary'">
              {{ $t(`partner-admin.statuses.${data.item.status}`) }}
            </b-badge>
          </template>

          <template #cell(updatedAt)="data">
            {{ formatDate(data.item.updatedAt) }}
          </template>

          <template #cell(actions)="data">
            <b-link :to="{ name: 'external-partner-details', params: { id: data.item.id } }">
              {{ $t('partner-admin.actions.open') }}
            </b-link>
          </template>
        </b-table>
      </rc-overlay>

      <table-bottom
        :query="query"
        :total="total"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BLink,
  BRow,
  BTable,
} from 'bootstrap-vue'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import TableBottom from '@/layouts/components/table/TableBottom.vue'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'

export default {
  name: 'ExternalPartnerList',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BLink,
    BRow,
    BTable,
    RcOverlay,
    TableBottom,
  },
  data() {
    return {
      loading: false,
      query: {
        q: null,
        status: null,
        limit: 25,
        page: 1,
        sortField: 'id',
        sortDir: 'ASC',
      },
      limitOptions: [10, 25, 50, 100].map(limit => ({ value: limit, text: limit })),
    }
  },
  computed: {
    partners() {
      return this.$store.getters['externalPartner/getPartners']
    },
    total() {
      return this.$store.getters['externalPartner/getPartnersTotal']
    },
    statuses() {
      return this.$store.getters['externalPartner/getExternalPartnerStatuses']
    },
    statusOptions() {
      return [
        {
          value: null,
          text: this.$t('partner-admin.filters.any-status'),
        },
        ...this.statuses.map(status => ({
          value: status,
          text: this.$t(`partner-admin.statuses.${status}`),
        })),
      ]
    },
    fields() {
      return [
        { key: 'id', label: this.$t('partner-admin.fields.id') },
        { key: 'name', label: this.$t('partner-admin.fields.name') },
        { key: 'code', label: this.$t('partner-admin.fields.code') },
        { key: 'status', label: this.$t('partner-admin.fields.status') },
        { key: 'updatedAt', label: this.$t('partner-admin.fields.updated-at') },
        { key: 'actions', label: this.$t('shared.actions') },
      ]
    },
  },
  watch: {
    query: {
      deep: true,
      handler() {
        this.fetchData()
      },
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.loading = true
      const payload = {
        limit: this.query.limit,
        page: this.query.page,
        sortField: this.query.sortField,
        sortDir: this.query.sortDir,
      }

      if (this.query.q) {
        payload.q = this.query.q
      }

      if (this.query.status) {
        payload.status = this.query.status
      }

      this.$store.dispatch('externalPartner/fetchPartners', payload)
        .catch(error => {
          parseRequestError(error)
          window.toast.notify.danger(this.$t('partner-admin.errors.fetch-list'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      return transformDatetimeToHumanReadablePretty(value)
    },
  },
}
</script>
